import { useCallback, useEffect, useRef, useState } from "react";
import { ModalOverlay } from "../../../components/ModalOverlay";

type ImageCropModalProps = {
  src: string;
  title: string;
  /** Fixed output size. Omit for free crop. */
  targetWidth?: number;
  targetHeight?: number;
  freeCrop?: boolean;
  onCancel: () => void;
  onConfirm: (dataUrl: string) => void;
};

type Rect = { x: number; y: number; w: number; h: number };
type DragMode =
  | "move"
  | "n"
  | "s"
  | "e"
  | "w"
  | "ne"
  | "nw"
  | "se"
  | "sw"
  | null;

const VIEW_MAX = 420;
const MIN_CROP = 24;

function clamp(n: number, min: number, max: number) {
  return Math.min(max, Math.max(min, n));
}

export default function ImageCropModal({
  src,
  title,
  targetWidth,
  targetHeight,
  freeCrop = false,
  onCancel,
  onConfirm,
}: ImageCropModalProps) {
  const isFree =
    freeCrop ||
    !(
      typeof targetWidth === "number" &&
      typeof targetHeight === "number" &&
      targetWidth > 0 &&
      targetHeight > 0
    );

  if (isFree) {
    return (
      <FreeCropModal src={src} title={title} onCancel={onCancel} onConfirm={onConfirm} />
    );
  }

  return (
    <FixedCropModal
      src={src}
      title={title}
      targetWidth={targetWidth!}
      targetHeight={targetHeight!}
      onCancel={onCancel}
      onConfirm={onConfirm}
    />
  );
}

function FixedCropModal({
  src,
  title,
  targetWidth,
  targetHeight,
  onCancel,
  onConfirm,
}: {
  src: string;
  title: string;
  targetWidth: number;
  targetHeight: number;
  onCancel: () => void;
  onConfirm: (dataUrl: string) => void;
}) {
  const [natural, setNatural] = useState({ w: 0, h: 0 });
  const [zoom, setZoom] = useState(1);
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const dragRef = useRef<{ startX: number; startY: number; ox: number; oy: number } | null>(
    null
  );

  const aspect = targetWidth / targetHeight;
  const frameW = Math.min(360, Math.max(180, aspect >= 1 ? 360 : 220));
  const frameH = frameW / aspect;

  useEffect(() => {
    const img = new Image();
    img.onload = () => {
      setNatural({ w: img.naturalWidth, h: img.naturalHeight });
      const cover = Math.max(frameW / img.naturalWidth, frameH / img.naturalHeight);
      setZoom(cover);
      setOffset({ x: 0, y: 0 });
    };
    img.src = src;
  }, [src, frameW, frameH]);

  const clampOffset = useCallback(
    (x: number, y: number, z: number) => {
      if (!natural.w || !natural.h) return { x: 0, y: 0 };
      const scaledW = natural.w * z;
      const scaledH = natural.h * z;
      const maxX = Math.max(0, (scaledW - frameW) / 2);
      const maxY = Math.max(0, (scaledH - frameH) / 2);
      return {
        x: Math.min(maxX, Math.max(-maxX, x)),
        y: Math.min(maxY, Math.max(-maxY, y)),
      };
    },
    [natural, frameW, frameH]
  );

  const onPointerDown = (e: React.PointerEvent) => {
    (e.target as HTMLElement).setPointerCapture?.(e.pointerId);
    dragRef.current = { startX: e.clientX, startY: e.clientY, ox: offset.x, oy: offset.y };
  };

  const onPointerMove = (e: React.PointerEvent) => {
    if (!dragRef.current) return;
    const dx = e.clientX - dragRef.current.startX;
    const dy = e.clientY - dragRef.current.startY;
    setOffset(clampOffset(dragRef.current.ox + dx, dragRef.current.oy + dy, zoom));
  };

  const onPointerUp = () => {
    dragRef.current = null;
  };

  const minZoom =
    natural.w && natural.h ? Math.max(frameW / natural.w, frameH / natural.h) : 1;

  const handleZoom = (value: number) => {
    const z = Math.max(minZoom, value);
    setZoom(z);
    setOffset((prev) => clampOffset(prev.x, prev.y, z));
  };

  const handleConfirm = () => {
    if (!natural.w || !natural.h) return;
    const canvas = document.createElement("canvas");
    canvas.width = targetWidth;
    canvas.height = targetHeight;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    const img = new Image();
    img.onload = () => {
      const scaledW = natural.w * zoom;
      const scaledH = natural.h * zoom;
      const imgLeft = (frameW - scaledW) / 2 + offset.x;
      const imgTop = (frameH - scaledH) / 2 + offset.y;
      const sx = (0 - imgLeft) / zoom;
      const sy = (0 - imgTop) / zoom;
      const sw = frameW / zoom;
      const sh = frameH / zoom;
      ctx.drawImage(img, sx, sy, sw, sh, 0, 0, targetWidth, targetHeight);
      onConfirm(canvas.toDataURL("image/png"));
    };
    img.src = src;
  };

  return (
    <ModalOverlay className="fixed inset-0 z-[1100] flex items-center justify-center bg-black/50 p-4">
      <div className="w-full max-w-lg overflow-hidden rounded-xl border border-slate-200 bg-white shadow-xl dark:border-slate-700 dark:bg-slate-800">
        <div className="border-b border-slate-200 px-4 py-3 dark:border-slate-700">
          <h3 className="text-sm font-semibold text-slate-800 dark:text-white">{title}</h3>
          <p className="mt-0.5 text-xs text-slate-500 dark:text-slate-400">
            Crop to {targetWidth}×{targetHeight}px — drag to position, zoom to fit
          </p>
        </div>
        <div className="flex flex-col items-center gap-4 p-4">
          <div
            className="relative cursor-move touch-none select-none overflow-hidden rounded-lg bg-slate-900"
            style={{ width: frameW, height: frameH }}
            onPointerDown={onPointerDown}
            onPointerMove={onPointerMove}
            onPointerUp={onPointerUp}
            onPointerCancel={onPointerUp}
          >
            {natural.w > 0 && (
              <img
                src={src}
                alt="Crop preview"
                draggable={false}
                className="pointer-events-none absolute max-w-none"
                style={{
                  width: natural.w * zoom,
                  height: natural.h * zoom,
                  left: (frameW - natural.w * zoom) / 2 + offset.x,
                  top: (frameH - natural.h * zoom) / 2 + offset.y,
                }}
              />
            )}
            <div className="pointer-events-none absolute inset-0 border-2 border-white/80" />
          </div>
          <label className="flex w-full max-w-sm flex-col gap-1">
            <span className="text-xs font-medium text-slate-600 dark:text-slate-400">Zoom</span>
            <input
              type="range"
              min={minZoom}
              max={minZoom * 3}
              step={0.01}
              value={zoom}
              onChange={(e) => handleZoom(Number(e.target.value))}
              className="w-full accent-primary"
            />
          </label>
        </div>
        <div className="flex justify-end gap-2 border-t border-slate-200 px-4 py-3 dark:border-slate-700">
          <button
            type="button"
            onClick={onCancel}
            className="rounded-lg px-3 py-1.5 text-sm font-medium text-slate-600 hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-slate-700"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleConfirm}
            className="rounded-lg bg-primary px-3 py-1.5 text-sm font-semibold text-white hover:bg-primary/95"
          >
            Apply crop
          </button>
        </div>
      </div>
    </ModalOverlay>
  );
}

function FreeCropModal({
  src,
  title,
  onCancel,
  onConfirm,
}: {
  src: string;
  title: string;
  onCancel: () => void;
  onConfirm: (dataUrl: string) => void;
}) {
  const [natural, setNatural] = useState({ w: 0, h: 0 });
  const [view, setView] = useState({ w: VIEW_MAX, h: VIEW_MAX });
  const [crop, setCrop] = useState<Rect>({ x: 0, y: 0, w: 0, h: 0 });
  const dragRef = useRef<{
    mode: Exclude<DragMode, null>;
    startX: number;
    startY: number;
    origin: Rect;
  } | null>(null);

  useEffect(() => {
    const img = new Image();
    img.onload = () => {
      const nw = img.naturalWidth;
      const nh = img.naturalHeight;
      setNatural({ w: nw, h: nh });
      const scale = Math.min(VIEW_MAX / nw, VIEW_MAX / nh, 1);
      const vw = Math.max(160, Math.round(nw * scale));
      const vh = Math.max(160, Math.round(nh * scale));
      setView({ w: vw, h: vh });
      const w = Math.round(vw * 0.8);
      const h = Math.round(vh * 0.8);
      setCrop({
        x: Math.round((vw - w) / 2),
        y: Math.round((vh - h) / 2),
        w,
        h,
      });
    };
    img.src = src;
  }, [src]);

  const normalizeCrop = useCallback(
    (next: Rect): Rect => {
      let { x, y, w, h } = next;
      w = clamp(w, MIN_CROP, view.w);
      h = clamp(h, MIN_CROP, view.h);
      x = clamp(x, 0, view.w - w);
      y = clamp(y, 0, view.h - h);
      return { x, y, w, h };
    },
    [view.w, view.h]
  );

  const onPointerDown = (mode: Exclude<DragMode, null>) => (e: React.PointerEvent) => {
    e.preventDefault();
    e.stopPropagation();
    (e.target as HTMLElement).setPointerCapture?.(e.pointerId);
    dragRef.current = {
      mode,
      startX: e.clientX,
      startY: e.clientY,
      origin: { ...crop },
    };
  };

  const onPointerMove = (e: React.PointerEvent) => {
    const drag = dragRef.current;
    if (!drag) return;
    const dx = e.clientX - drag.startX;
    const dy = e.clientY - drag.startY;
    const o = drag.origin;
    let next = { ...o };

    switch (drag.mode) {
      case "move":
        next = { ...o, x: o.x + dx, y: o.y + dy };
        break;
      case "e":
        next = { ...o, w: o.w + dx };
        break;
      case "w":
        next = { ...o, x: o.x + dx, w: o.w - dx };
        break;
      case "s":
        next = { ...o, h: o.h + dy };
        break;
      case "n":
        next = { ...o, y: o.y + dy, h: o.h - dy };
        break;
      case "se":
        next = { ...o, w: o.w + dx, h: o.h + dy };
        break;
      case "sw":
        next = { ...o, x: o.x + dx, w: o.w - dx, h: o.h + dy };
        break;
      case "ne":
        next = { ...o, w: o.w + dx, y: o.y + dy, h: o.h - dy };
        break;
      case "nw":
        next = { ...o, x: o.x + dx, w: o.w - dx, y: o.y + dy, h: o.h - dy };
        break;
      default:
        break;
    }

    if (next.w < MIN_CROP) {
      if (drag.mode.includes("w")) next.x = o.x + o.w - MIN_CROP;
      next.w = MIN_CROP;
    }
    if (next.h < MIN_CROP) {
      if (drag.mode.includes("n")) next.y = o.y + o.h - MIN_CROP;
      next.h = MIN_CROP;
    }

    setCrop(normalizeCrop(next));
  };

  const onPointerUp = () => {
    dragRef.current = null;
  };

  const handleConfirm = () => {
    if (!natural.w || !natural.h || !crop.w || !crop.h) return;
    const scaleX = natural.w / view.w;
    const scaleY = natural.h / view.h;
    const sx = crop.x * scaleX;
    const sy = crop.y * scaleY;
    const sw = crop.w * scaleX;
    const sh = crop.h * scaleY;
    const outW = Math.max(1, Math.round(sw));
    const outH = Math.max(1, Math.round(sh));

    const img = new Image();
    img.onload = () => {
      const canvas = document.createElement("canvas");
      canvas.width = outW;
      canvas.height = outH;
      const ctx = canvas.getContext("2d");
      if (!ctx) return;
      ctx.drawImage(img, sx, sy, sw, sh, 0, 0, outW, outH);
      onConfirm(canvas.toDataURL("image/png"));
    };
    img.src = src;
  };

  const handle = (mode: Exclude<DragMode, null>, className: string) => (
    <div
      key={mode}
      role="presentation"
      className={`absolute z-10 touch-none ${className}`}
      onPointerDown={onPointerDown(mode)}
    />
  );

  return (
    <ModalOverlay className="fixed inset-0 z-[1100] flex items-center justify-center bg-black/50 p-4">
      <div className="w-full max-w-xl overflow-hidden rounded-xl border border-slate-200 bg-white shadow-xl dark:border-slate-700 dark:bg-slate-800">
        <div className="border-b border-slate-200 px-4 py-3 dark:border-slate-700">
          <h3 className="text-sm font-semibold text-slate-800 dark:text-white">{title}</h3>
          <p className="mt-0.5 text-xs text-slate-500 dark:text-slate-400">
            Free crop — drag box to move, edges/corners to resize, or use original
          </p>
        </div>
        <div className="flex flex-col items-center gap-3 p-4">
          <div
            className="relative touch-none select-none overflow-hidden rounded-lg bg-slate-900"
            style={{ width: view.w, height: view.h }}
            onPointerMove={onPointerMove}
            onPointerUp={onPointerUp}
            onPointerCancel={onPointerUp}
          >
            {natural.w > 0 ? (
              <img
                src={src}
                alt="Crop source"
                draggable={false}
                className="pointer-events-none absolute inset-0 h-full w-full object-fill"
              />
            ) : null}
            <div
              className="pointer-events-none absolute left-0 right-0 top-0 bg-black/45"
              style={{ height: Math.max(0, crop.y) }}
            />
            <div
              className="pointer-events-none absolute bottom-0 left-0 right-0 bg-black/45"
              style={{ height: Math.max(0, view.h - crop.y - crop.h) }}
            />
            <div
              className="pointer-events-none absolute left-0 bg-black/45"
              style={{ top: crop.y, height: crop.h, width: Math.max(0, crop.x) }}
            />
            <div
              className="pointer-events-none absolute right-0 bg-black/45"
              style={{
                top: crop.y,
                height: crop.h,
                width: Math.max(0, view.w - crop.x - crop.w),
              }}
            />
            <div
              className="absolute cursor-move border-2 border-white shadow-[0_0_0_1px_rgba(0,0,0,0.35)]"
              style={{ left: crop.x, top: crop.y, width: crop.w, height: crop.h }}
              onPointerDown={onPointerDown("move")}
            >
              <div className="pointer-events-none absolute inset-0 grid grid-cols-3 grid-rows-3">
                {Array.from({ length: 9 }).map((_, i) => (
                  <div key={i} className="border border-white/25" />
                ))}
              </div>
              {handle("n", "left-2 right-2 top-0 h-3 -translate-y-1/2 cursor-ns-resize")}
              {handle("s", "left-2 right-2 bottom-0 h-3 translate-y-1/2 cursor-ns-resize")}
              {handle("w", "top-2 bottom-2 left-0 w-3 -translate-x-1/2 cursor-ew-resize")}
              {handle("e", "top-2 bottom-2 right-0 w-3 translate-x-1/2 cursor-ew-resize")}
              {handle(
                "nw",
                "left-0 top-0 h-3.5 w-3.5 -translate-x-1/2 -translate-y-1/2 cursor-nwse-resize rounded-sm bg-white"
              )}
              {handle(
                "ne",
                "right-0 top-0 h-3.5 w-3.5 translate-x-1/2 -translate-y-1/2 cursor-nesw-resize rounded-sm bg-white"
              )}
              {handle(
                "sw",
                "left-0 bottom-0 h-3.5 w-3.5 -translate-x-1/2 translate-y-1/2 cursor-nesw-resize rounded-sm bg-white"
              )}
              {handle(
                "se",
                "right-0 bottom-0 h-3.5 w-3.5 translate-x-1/2 translate-y-1/2 cursor-nwse-resize rounded-sm bg-white"
              )}
            </div>
          </div>
          {crop.w > 0 ? (
            <p className="text-[11px] text-slate-500 dark:text-slate-400">
              Selection {Math.round((crop.w / view.w) * natural.w)}×
              {Math.round((crop.h / view.h) * natural.h)}px
            </p>
          ) : null}
        </div>
        <div className="flex flex-wrap justify-end gap-2 border-t border-slate-200 px-4 py-3 dark:border-slate-700">
          <button
            type="button"
            onClick={onCancel}
            className="rounded-lg px-3 py-1.5 text-sm font-medium text-slate-600 hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-slate-700"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() => onConfirm(src)}
            className="rounded-lg border border-slate-200 px-3 py-1.5 text-sm font-medium text-slate-700 hover:bg-slate-50 dark:border-slate-600 dark:text-slate-200 dark:hover:bg-slate-700"
          >
            Use original
          </button>
          <button
            type="button"
            onClick={handleConfirm}
            className="rounded-lg bg-primary px-3 py-1.5 text-sm font-semibold text-white hover:bg-primary/95"
          >
            Apply crop
          </button>
        </div>
      </div>
    </ModalOverlay>
  );
}
